import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Select, Space, Spin, Typography } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { LocationDTO, LocationConfigDTO } from '@/shared/api/model';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';

const { Text } = Typography;

type LocationLevel = 'zone' | 'region' | 'district' | 'ward' | 'street';
type LocationSelections = Partial<Record<LocationLevel, LocationDTO>>;

type Props = {
  addressType: string;
  onLocationSelected?: (locationData: {
    locationSelections: LocationSelections;
    gpsCoordinates: Record<string, never>;
    houseNumber: string;
    postalCode: string;
    isPrimary: boolean;
    validationErrors: string[];
  }) => void;
  disabled?: boolean;
  initialValues?: {
    locationSelections?: LocationSelections;
    houseNumber?: string;
    postalCode?: string;
    isPrimary?: boolean;
  };
};

const endpoint = '/frontend/v1/self-registration/locations';

const SelfRegistrationLocationSelector: React.FC<Props> = ({
  addressType,
  onLocationSelected,
  disabled = false,
  initialValues
}) => {
  const [config, setConfig] = useState<LocationConfigDTO | null>(null);
  const [selections, setSelections] = useState<LocationSelections>(initialValues?.locationSelections || {});
  const [locations, setLocations] = useState<Record<LocationLevel, LocationDTO[]>>({
    zone: [], region: [], district: [], ward: [], street: []
  });
  const [houseNumber, setHouseNumber] = useState(initialValues?.houseNumber || '');
  const [postalCode, setPostalCode] = useState(initialValues?.postalCode || '');
  const [isPrimary, setIsPrimary] = useState(initialValues?.isPrimary ?? true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    AXIOS_INSTANCE.get<any>(`${endpoint}/config/CUSTOMER`)
      .then(({ data }) => {
        if (!active) return;
        const configs: LocationConfigDTO[] = Array.isArray(data) ? data : (data?.data ?? []);
        setConfig(configs.find(item => item.addressType === addressType && item.isActive) || null);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [addressType]);

  const mandatoryLevels = useMemo(() => {
    const result: Record<LocationLevel, boolean> = {
      zone: false, region: false, district: false, ward: false, street: false
    };
    config?.configJson?.requiredLevels?.forEach(level => {
      const key = level.level.toLowerCase() as LocationLevel;
      if (key in result) result[key] = level.mandatory === true;
    });
    return result;
  }, [config]);

  const visibleLevels = useMemo(() => {
    const result = { ...mandatoryLevels };
    if (!result.region) {
      result.district = false;
      result.ward = false;
      result.street = false;
    }
    if (!result.district) {
      result.ward = false;
      result.street = false;
    }
    if (!result.ward) result.street = false;
    return result;
  }, [mandatoryLevels]);

  const loadLocations = async (level: LocationLevel, parentId?: number) => {
    const { data } = await AXIOS_INSTANCE.get<any>(
      parentId == null ? `${endpoint}/children` : `${endpoint}/children/${parentId}`
    );
    const items: LocationDTO[] = Array.isArray(data) ? data : (data?.data ?? []);
    setLocations(current => ({ ...current, [level]: items }));
  };

  useEffect(() => {
    if (config) void loadLocations('zone');
  }, [config]);

  const levelOrder: LocationLevel[] = ['zone', 'region', 'district', 'ward', 'street'];

  const handleChange = async (level: LocationLevel, id: number) => {
    const index = levelOrder.indexOf(level);
    const selected = locations[level].find(item => item.id === id);
    const nextSelections = { ...selections, [level]: selected };
    levelOrder.slice(index + 1).forEach(child => delete nextSelections[child]);
    setSelections(nextSelections);
    levelOrder.slice(index + 1).forEach(child => setLocations(current => ({ ...current, [child]: [] })));
    const nextLevel = levelOrder[index + 1];
    if (nextLevel && selected?.id != null && visibleLevels[nextLevel]) {
      await loadLocations(nextLevel, selected.id);
    }
    emit(nextSelections, houseNumber, postalCode, isPrimary);
  };

  const emit = (nextSelections: LocationSelections, nextHouseNumber: string, nextPostalCode: string, nextIsPrimary: boolean) => {
    const errors = levelOrder
      .filter(level => mandatoryLevels[level] && visibleLevels[level] && !nextSelections[level]?.id)
      .map(level => `${level.charAt(0).toUpperCase() + level.slice(1)} is required`);
    if (config?.configJson?.validation?.requireHouseNumber && !nextHouseNumber.trim()) errors.push('House number is required');
    if (config?.configJson?.validation?.requirePostalCode && !nextPostalCode.trim()) errors.push('Postal code is required');
    onLocationSelected?.({
      locationSelections: nextSelections,
      gpsCoordinates: {},
      houseNumber: nextHouseNumber,
      postalCode: nextPostalCode,
      isPrimary: nextIsPrimary,
      validationErrors: errors
    });
  };

  const updateField = (field: 'houseNumber' | 'postalCode' | 'isPrimary', value: string | boolean) => {
    const nextHouseNumber = field === 'houseNumber' ? value as string : houseNumber;
    const nextPostalCode = field === 'postalCode' ? value as string : postalCode;
    const nextIsPrimary = field === 'isPrimary' ? value as boolean : isPrimary;
    if (field === 'houseNumber') setHouseNumber(nextHouseNumber);
    if (field === 'postalCode') setPostalCode(nextPostalCode);
    if (field === 'isPrimary') setIsPrimary(nextIsPrimary);
    emit(selections, nextHouseNumber, nextPostalCode, nextIsPrimary);
  };

  if (loading) return <Card><div className="flex justify-center p-4"><Spin /></div></Card>;

  return (
    <Card title={<Space><EnvironmentOutlined /><Text>{addressType} Location</Text></Space>}>
      <Form layout="vertical">
        {levelOrder.map((level, index) => visibleLevels[level] && (
          <Form.Item key={level} label={level.charAt(0).toUpperCase() + level.slice(1)} required={mandatoryLevels[level]}>
            <Select
              value={selections[level]?.id}
              placeholder={`Select ${level}`}
              disabled={disabled || (index > 0 && !selections[levelOrder[index - 1]])}
              loading={!locations[level].length && (index === 0 || !!selections[levelOrder[index - 1]])}
              onChange={value => void handleChange(level, value)}
              showSearch
              optionFilterProp="label"
              options={locations[level].map(location => ({ value: location.id, label: location.name }))}
            />
          </Form.Item>
        ))}
        {config?.configJson?.validation?.requireHouseNumber && (
          <Form.Item label="House Number / Plot Number" required>
            <Input value={houseNumber} onChange={event => updateField('houseNumber', event.target.value)} disabled={disabled} />
          </Form.Item>
        )}
        {config?.configJson?.validation?.requirePostalCode && (
          <Form.Item label="Postal Code" required>
            <Input value={postalCode} onChange={event => updateField('postalCode', event.target.value)} disabled={disabled} />
          </Form.Item>
        )}
        <Form.Item>
          <label>
            <input type="checkbox" checked={isPrimary} onChange={event => updateField('isPrimary', event.target.checked)} disabled={disabled} /> Set as primary address
          </label>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default SelfRegistrationLocationSelector;
